import { useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Sparkles, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import PreferenceSelector from "@/components/PreferenceSelector";
import BookCard from "@/components/BookCard";
import BookAutocomplete from "@/components/BookAutocomplete";
import CountrySelector from "@/components/CountrySelector";
import NearbyBookstores from "@/components/NearbyBookstores";
import ShareButton from "@/components/ShareButton";
import ShareReadingListCTA from "@/components/ShareReadingListCTA";
import ReadingStats from "@/components/ReadingStats";
import SkeletonResults from "@/components/SkeletonResults";
import { useAuth } from "@/contexts/AuthContext";
import { useReading } from "@/contexts/ReadingContext";

export default function Home() {
    const { t, i18n } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [bookTitle, setBookTitle] = useState("");
    // Captured author from autocomplete pick. Sent to the backend so the
    // LLM disambiguates homonymous titles (e.g. "El oficio de vivir" exists
    // for both Pavese and Enríquez). Cleared whenever the user manually
    // edits the title — at that point we no longer trust the prior author
    // binding.
    const [bookAuthor, setBookAuthor] = useState("");
    const [preference, setPreference] = useState("similar");
    const [pagesPref, setPagesPref] = useState("medium");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const { user } = useAuth();
    const { refresh: refreshReading } = useReading();
    const resultsRef = useRef(null);
    const [visibleCount, setVisibleCount] = useState(3);
    // Cumulative set of book titles shown across the current "session" so
    // the LLM never repeats one already seen. We keep the LAST `based_on`
    // we generated for too — when the user types a NEW input, the set is
    // reset (otherwise the exclusions would carry over indefinitely and
    // exhaust the pool).
    const sessionShownRef = useRef({ basedOn: "", titles: new Set() });

    useEffect(() => {
        if ((loading || result) && resultsRef.current) {
            resultsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, [loading, result]);

    // Deep-link support: visiting `/?book_title=Cien%20a%C3%B1os%20de%20soledad`
    // (e.g. from a blog post link) prefills the search and auto-fires the
    // recommender. Clears the param after firing so reload doesn't re-run.
    useEffect(() => {
        const incoming = searchParams.get("book_title");
        if (!incoming) return;
        const trimmed = incoming.trim();
        if (!trimmed) return;
        setBookTitle(trimmed);
        // Use a microtask so React state settles before the request fires.
        setTimeout(() => handleSubmit(null, trimmed), 0);
        // Strip the param so the URL becomes canonical /.
        searchParams.delete("book_title");
        setSearchParams(searchParams, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmit = async (e, overrideTitle = null, isRefresh = false, overrideAuthor = null) => {
        e?.preventDefault();
        const title = (overrideTitle ?? bookTitle).trim();
        // Author is only authoritative on autocomplete pick / refresh — on a
        // free-typed submit (`overrideTitle == null` && `overrideAuthor == null`)
        // we trust the current `bookAuthor` state (last picked) only if the
        // title still matches it; otherwise the user has clearly retyped.
        const author = (overrideAuthor ?? (overrideTitle ? "" : bookAuthor) ?? "").trim();
        if (!title) {
            toast.error(t("toast.title_required"));
            return;
        }

        // Build the cumulative exclusion list. If this is a refresh on the
        // SAME `based_on`, keep accumulating; if it's a brand new query
        // (user typed something different), reset the session set.
        const session = sessionShownRef.current;
        const titleKey = title.toLowerCase();
        if (!isRefresh || session.basedOn !== titleKey) {
            session.basedOn = titleKey;
            session.titles = new Set();
        }
        // Backend caps exclude_titles at 30. Keep the most recent 30 if we
        // somehow exceed (e.g. user refreshes 6 times → 36 books).
        const allShown = Array.from(session.titles);
        const excludeTitles = allShown.slice(-30);

        setLoading(true);
        setResult(null);
        setVisibleCount(3);
        try {
            const { data } = await api.post("/recommend", {
                book_title: title,
                book_author: author || null,
                preference,
                pages_preference: preference === "by_pages" ? pagesPref : null,
                locale: (i18n.language || "en").split("-")[0],
                exclude_titles: excludeTitles,
            });
            setResult(data);
            // Stamp the freshly returned titles into the cumulative session
            // set for the next refresh.
            (data?.recommendations || []).forEach((b) => {
                if (b?.title) session.titles.add(b.title);
            });
            refreshReading();
            if (user && typeof user === "object") {
                toast.success(t("auth.toast_recommend_saved"));
            }
        } catch (err) {
            // 422 with code 'no_verified_results' is a structured signal from
            // the anti-hallucination layer: every book failed cross-source
            // verification (OL + Hardcover + Google Books all said "doesn't
            // exist"). Show a clear actionable message instead of the
            // generic "request failed" toast.
            const detail = err.response?.data?.detail;
            if (
                err.response?.status === 422 &&
                typeof detail === "object" &&
                detail?.code === "no_verified_results"
            ) {
                toast.error(t("toast.no_verified_results"), {
                    description: t("toast.no_verified_results_hint"),
                    duration: 6000,
                });
            } else {
                toast.error(
                    formatApiError(detail) || t("toast.recommend_failed")
                );
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <main className="min-h-[calc(100vh-73px)]">
            <section className="max-w-5xl mx-auto px-6 md:px-12 pt-20 md:pt-32 pb-16 md:pb-24">
                <div className="flex items-center gap-2 justify-center mb-8">
                    <div className="h-px w-10 bg-[var(--accent)]/40 hidden sm:block" />
                    <span className="text-xs uppercase tracking-[0.3em] text-[var(--accent)] font-medium text-center">
                        {t("home.eyebrow")}
                    </span>
                    <div className="h-px w-10 bg-[var(--accent)]/40 hidden sm:block" />
                </div>

                <h1
                    data-testid="hero-title"
                    className="font-display text-5xl sm:text-6xl md:text-7xl text-[var(--ink)] text-center leading-[1.05] tracking-tight mb-6"
                    style={{ fontVariationSettings: '"wght" 600, "wdth" 80, "opsz" 144' }}
                >
                    {t("home.title_line1")}
                    <br />
                    <span className="italic text-[var(--accent)]" style={{ fontVariationSettings: '"wght" 500, "wdth" 90, "opsz" 144, "slnt" -10' }}>
                        {t("home.title_line2")}
                    </span>
                </h1>
                <p className="text-center text-[var(--ink-muted)] max-w-2xl mx-auto text-lg leading-relaxed mb-14 md:mb-20">
                    {t("home.lead")}
                </p>

                <form onSubmit={handleSubmit} className="relative">
                    <BookAutocomplete
                        value={bookTitle}
                        onChange={(v) => {
                            setBookTitle(v);
                            // User edited the title manually → clear the
                            // bound author so we don't ship a stale pairing
                            // to the LLM (e.g. picked "El oficio de vivir"
                            // by Enríquez, then retyped to "El oficio…").
                            if (bookAuthor) setBookAuthor("");
                        }}
                        onPick={(item) => {
                            // Tap-to-recommend: picking a suggestion auto-fires
                            // the form with the canonical title + author from
                            // Google Books, skipping the extra "now press
                            // Submit" step. Passing the author disambiguates
                            // homonymous titles for the LLM.
                            setBookAuthor(item.author || "");
                            handleSubmit(null, item.title, false, item.author || "");
                        }}
                    />
                    <p className="text-xs text-[var(--ink-dim)] mt-2 uppercase tracking-widest">
                        {t("home.input_hint")}
                    </p>

                    <div className="mt-12 md:mt-16">
                        <p className="text-center text-xs uppercase tracking-[0.3em] text-[var(--ink-muted)] mb-6">
                            {t("home.preferences_eyebrow")}
                        </p>
                        <PreferenceSelector
                            value={preference}
                            onChange={setPreference}
                            pagesValue={pagesPref}
                            onPagesChange={setPagesPref}
                        />
                    </div>

                    <div className="flex justify-center mt-12">
                        <button
                            data-testid="submit-recommend-btn"
                            type="submit"
                            disabled={loading}
                            className="group flex items-center gap-2.5 bg-[var(--ink)] text-[var(--bg)] rounded-full hover:bg-[var(--accent)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors px-10 py-4 text-sm uppercase tracking-widest"
                        >
                            <Sparkles size={16} strokeWidth={1.5} />
                            <span>{loading ? t("home.cta_loading") : t("home.cta")}</span>
                        </button>
                    </div>
                </form>
            </section>

            {loading && (
                <div ref={resultsRef} data-testid="loading-state">
                    <SkeletonResults />
                </div>
            )}

            {result && !loading && (
                <section
                    ref={resultsRef}
                    data-testid="recommendations-section"
                    className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pb-24 md:pb-32"
                >
                    <div className="border-t border-[var(--border)] pt-16 mb-12">
                        <p className="text-xs uppercase tracking-[0.3em] text-[var(--accent)] mb-3">
                            {t("home.results_eyebrow")}
                        </p>
                        <h2
                            className="font-display text-4xl md:text-5xl text-[var(--ink)] italic mb-6"
                            style={{ fontVariationSettings: '"wght" 500, "wdth" 90, "opsz" 96, "slnt" -10' }}
                        >
                            {result.based_on}
                        </h2>
                        <div className="flex flex-wrap items-center gap-3">
                            <ShareButton recId={result.id} basedOn={result.based_on} />
                            <button
                                type="button"
                                data-testid="refresh-recs-btn"
                                onClick={() => handleSubmit(null, result.based_on, true, bookAuthor)}
                                disabled={loading}
                                className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-widest border border-[var(--border-strong)] hover:border-[var(--ink)] hover:bg-[var(--bg-hover)] text-[var(--ink-muted)] hover:text-[var(--ink)] rounded-full transition-all disabled:opacity-50"
                            >
                                <RefreshCw size={12} strokeWidth={1.8} />
                                {t("home.refresh_recs")}
                            </button>
                            <span className="text-xs text-[var(--ink-dim)]">
                                {t("home.results_share_hint")}
                            </span>
                        </div>
                    </div>

                    <div className="mb-10">
                        <ReadingStats />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
                        {result.recommendations.slice(0, visibleCount).map((book, i) => (
                            <BookCard
                                key={book.id}
                                book={book}
                                index={i}
                                recommendationId={result.id}
                            />
                        ))}
                    </div>

                    {result.recommendations.length > visibleCount && (
                        <div className="flex justify-center mt-10">
                            <button
                                type="button"
                                data-testid="load-more-recs-btn"
                                onClick={() =>
                                    setVisibleCount((n) =>
                                        Math.min(n + 3, result.recommendations.length)
                                    )
                                }
                                className="inline-flex items-center gap-2 px-6 py-3 text-xs uppercase tracking-widest border border-[var(--border-strong)] hover:border-[var(--ink)] hover:bg-[var(--bg-hover)] text-[var(--ink-muted)] hover:text-[var(--ink)] rounded-full transition-all"
                            >
                                {t("home.load_more", {
                                    count: Math.min(3, result.recommendations.length - visibleCount),
                                })}
                            </button>
                        </div>
                    )}

                    <ShareReadingListCTA result={result} />

                    <NearbyBookstores />

                    <p className="text-center text-xs text-[var(--ink-dim)] mt-16 max-w-xl mx-auto leading-relaxed">
                        {t("home.affiliate_disclaimer")}
                    </p>
                    <div className="mt-6 flex justify-center">
                        <CountrySelector />
                    </div>
                </section>
            )}

            {!result && !loading && (
                <section className="max-w-4xl mx-auto px-6 md:px-12 pb-24 md:pb-32">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10 text-left border-t border-[var(--border)] pt-16">
                        {[1, 2, 3, 4].map((n) => (
                            <div key={n}>
                                <p
                                    className="font-display text-5xl text-[var(--accent)]/50 mb-3"
                                    style={{ fontVariationSettings: '"wght" 700, "wdth" 75, "opsz" 144' }}
                                >
                                    0{n}
                                </p>
                                <h3
                                    className="font-display text-2xl text-[var(--ink)] mb-2"
                                    style={{ fontVariationSettings: '"wght" 600, "wdth" 95, "opsz" 32' }}
                                >
                                    {t(`home.step_${n}_title`)}
                                </h3>
                                <p className="text-sm text-[var(--ink-muted)] leading-relaxed">
                                    {t(`home.step_${n}_desc`)}
                                </p>
                            </div>
                        ))}
                    </div>
                </section>
            )}
        </main>
    );
}
